import { useQuery } from "@tanstack/react-query";
import { Gift, HandCoins, Handshake, Plus } from "lucide-react";
import { type FormEvent, useState } from "react";
import { api, jsonBody, queryString } from "../../api/client";
import type { EventRecord, ListResponse, Sponsor, Sponsorship } from "../../api/types";
import { useTenant } from "../../app/tenant";
import { useVenueScope } from "../../app/venueScope";
import {
  formatDate,
  formatMoney,
  formatQuantity,
  titleCase,
  zonedInputToISO,
} from "../../lib/format";
import {
  Card,
  CardHeader,
  DataTable,
  EmptyState,
  ErrorPanel,
  Field,
  FormActions,
  Metric,
  Modal,
  Notice,
  PageHeader,
  ReasonDialog,
  StatusPill,
} from "../../ui/components";
import { ListPageSkeleton } from "../../ui/skeletons";
import { ContactPicker } from "../contacts/ContactPicker";
import { useVenues } from "../venues/queries";

interface Contribution {
  id: string;
  sponsorshipId: string;
  kind: "cash" | "in_kind";
  description: string;
  cashAmountMinor?: number;
  estimatedValueMinor?: number;
  currency: string;
  quantityMilli?: number;
  status: string;
  restricted: boolean;
  availableOn?: string;
  valuationDate?: string;
  valuationSource: string;
  receivedAt?: string;
  // Set once a payment request has been raised for this pledge. The invoice collects the
  // money; the contribution keeps the sponsorship story.
  invoiceId?: string;
  invoiceNumber: string;
  version: number;
  createdAt: string;
}
interface Recognition {
  id: string;
  sponsorshipId: string;
  type: string;
  description: string;
  dueAt?: string;
  status: string;
  fulfilledAt?: string;
  version: number;
  createdAt: string;
}

const contributionTransitions: Record<string, string[]> = {
  pledged: ["pledged", "invoiced", "scheduled", "received", "cancelled"],
  invoiced: ["invoiced", "scheduled", "received", "cancelled"],
  scheduled: ["scheduled", "received", "cancelled"],
  received: ["received", "accepted", "refunded", "returned"],
  accepted: ["accepted", "consumed", "returned", "written_off"],
  consumed: ["consumed", "returned", "written_off"],
  returned: ["returned"],
  refunded: ["refunded"],
  written_off: ["written_off"],
  cancelled: ["cancelled"],
};

const recognitionTransitions: Record<string, string[]> = {
  open: ["open", "in_progress", "fulfilled", "waived", "overdue"],
  in_progress: ["in_progress", "open", "fulfilled", "waived", "overdue"],
  overdue: ["overdue", "in_progress", "fulfilled", "waived"],
  fulfilled: ["fulfilled"],
  waived: ["waived"],
};

export function SponsorsPage() {
  const [form, setForm] = useState<"sponsor" | "agreement" | "contribution" | "recognition" | null>(
    null,
  );
  const [selected, setSelected] = useState<Sponsorship | null>(null);
  const [kind, setKind] = useState<"cash" | "in_kind">("cash");
  const [error, setError] = useState<unknown>();
  const [working, setWorking] = useState(false);
  const [transition, setTransition] = useState<
    | { kind: "contribution"; item: Contribution; status: string }
    | { kind: "recognition"; item: Recognition; status: string }
    | null
  >(null);
  const [requesting, setRequesting] = useState<Contribution | null>(null);
  const [paymentLink, setPaymentLink] = useState<{ url: string; invoiceNumber: string } | null>(
    null,
  );
  const { selectedVenueId, loading: venueLoading } = useVenueScope();
  const tenant = useTenant();
  const tenantCurrency = tenant.data?.currency || "USD";
  const tenantTimezone = tenant.data?.timezone || "UTC";
  const sponsors = useQuery({
    queryKey: ["sponsors"],
    queryFn: () => api<ListResponse<Sponsor>>("/api/v1/sponsors"),
  });
  // Agreements are read through the venue selected in the top bar; sponsor companies
  // stay tenant-wide because a brand is not a room.
  const sponsorships = useQuery({
    queryKey: ["sponsorships", selectedVenueId],
    queryFn: () =>
      api<ListResponse<Sponsorship>>(
        `/api/v1/sponsorships${queryString({ venueId: selectedVenueId })}`,
      ),
    enabled: Boolean(selectedVenueId),
  });
  const venues = useVenues();
  // Sponsorship belongs to the event side: a sponsor pays to be seen by the people the client
  // gathers. A third party who hires a room and brings their own sponsors manages those themselves,
  // so the picker offers events and never bookings. The endpoint's own window, a month back and six
  // months ahead, is the right amount of it to choose from.
  const events = useQuery({
    queryKey: ["events", "sponsorship-picker"],
    queryFn: () => api<ListResponse<EventRecord>>("/api/v1/events"),
  });
  const contributions = useQuery({
    queryKey: ["sponsorships", selected?.id, "contributions"],
    queryFn: () =>
      api<ListResponse<Contribution>>(`/api/v1/sponsorships/${selected?.id}/contributions`),
    enabled: Boolean(selected),
  });
  const recognitions = useQuery({
    queryKey: ["sponsorships", selected?.id, "recognitions"],
    queryFn: () =>
      api<ListResponse<Recognition>>(`/api/v1/sponsorships/${selected?.id}/recognitions`),
    enabled: Boolean(selected),
  });

  if (venueLoading || sponsors.isLoading || (Boolean(selectedVenueId) && sponsorships.isPending))
    return (
      <ListPageSkeleton
        title="Sponsors"
        description="Sponsor agreements, contributions, and recognition commitments for your events."
        filterCount={2}
        columnCount={5}
      />
    );
  if (sponsors.error || sponsorships.error)
    return <ErrorPanel error={sponsors.error ?? sponsorships.error} />;
  const allContributions = selected ? (contributions.data?.items ?? []) : [];
  const cashReceived = allContributions
    .filter(
      (item) =>
        item.kind === "cash" &&
        ["received", "accepted", "consumed", "written_off"].includes(item.status),
    )
    .reduce((sum, item) => sum + (item.cashAmountMinor ?? 0), 0);
  const cashPledged = allContributions
    .filter(
      (item) =>
        item.kind === "cash" && !["cancelled", "refunded", "returned"].includes(item.status),
    )
    .reduce((sum, item) => sum + (item.cashAmountMinor ?? 0), 0);
  const inKindValue = allContributions
    .filter((item) => item.kind === "in_kind" && item.status !== "cancelled")
    .reduce((sum, item) => sum + (item.estimatedValueMinor ?? 0), 0);

  async function createSponsor(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    setWorking(true);
    setError(undefined);
    try {
      await api("/api/v1/sponsors", {
        method: "POST",
        body: jsonBody({
          name: String(data.get("name")),
          contact: {
            name: String(data.get("contactName")),
            email: String(data.get("email")),
            phone: String(data.get("phone")),
          },
          notes: String(data.get("notes")),
          venueIds: data.get("venueId") ? [String(data.get("venueId"))] : [],
        }),
      });
      await sponsors.refetch();
      setForm(null);
    } catch (caught) {
      setError(caught);
    } finally {
      setWorking(false);
    }
  }

  async function createAgreement(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const starts = String(data.get("startsOn"));
    const ends = String(data.get("endsOn"));
    const renewal = String(data.get("renewalOn"));
    const eventId = data.get("eventId") ? String(data.get("eventId")) : null;
    const venueId = data.get("venueId") ? String(data.get("venueId")) : null;
    // An agreement has to name what it sponsors, because one that names nothing cannot be reported
    // on afterwards. The server refuses it either way; saying so here saves a round trip nobody
    // learns anything from.
    if (!eventId && !venueId) {
      setError(
        new Error("Choose the event this sponsors, or the venue it is a standing arrangement for."),
      );
      return;
    }
    const payload = {
      sponsorId: String(data.get("sponsorId")),
      bookingId: null,
      eventId,
      venueId,
      name: String(data.get("name")),
      status: String(data.get("status")),
      startsOn: starts ? new Date(`${starts}T00:00:00Z`).toISOString() : null,
      endsOn: ends ? new Date(`${ends}T00:00:00Z`).toISOString() : null,
      agreementNumber: String(data.get("agreementNumber")),
      restrictions: { notes: String(data.get("restrictions")) },
      ownerUserId: null,
      renewalOn: renewal ? new Date(`${renewal}T00:00:00Z`).toISOString() : null,
    };
    setWorking(true);
    setError(undefined);
    try {
      await api("/api/v1/sponsorships", { method: "POST", body: jsonBody(payload) });
      await sponsorships.refetch();
      setForm(null);
    } catch (caught) {
      setError(caught);
    } finally {
      setWorking(false);
    }
  }

  async function createContribution(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!selected) return;
    const data = new FormData(event.currentTarget);
    const dollars = Math.round(Number(data.get("value")) * 100);
    const quantity = String(data.get("quantity"));
    const available = String(data.get("availableOn"));
    const payload = {
      kind,
      description: String(data.get("description")),
      cashAmountMinor: kind === "cash" ? dollars : null,
      estimatedValueMinor: kind === "in_kind" ? dollars : null,
      currency: tenantCurrency,
      quantityMilli: quantity ? Math.round(Number(quantity) * 1000) : null,
      status: String(data.get("status")),
      restricted: data.get("restricted") === "on",
      availableOn: available ? new Date(`${available}T00:00:00Z`).toISOString() : null,
      valuationDate: kind === "in_kind" ? new Date().toISOString() : null,
      valuationSource: kind === "in_kind" ? String(data.get("valuationSource")) : "",
    };
    setWorking(true);
    setError(undefined);
    try {
      await api(`/api/v1/sponsorships/${selected.id}/contributions`, {
        method: "POST",
        body: jsonBody(payload),
      });
      await contributions.refetch();
      setForm(null);
    } catch (caught) {
      setError(caught);
    } finally {
      setWorking(false);
    }
  }

  async function createRecognition(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!selected) return;
    const data = new FormData(event.currentTarget);
    const due = String(data.get("dueAt"));
    const timezone =
      venues.data?.items.find((venue) => venue.id === selected.venueId)?.timezone ?? tenantTimezone;
    setWorking(true);
    setError(undefined);
    try {
      await api(`/api/v1/sponsorships/${selected.id}/recognitions`, {
        method: "POST",
        body: jsonBody({
          type: String(data.get("type")),
          description: String(data.get("description")),
          ownerUserId: null,
          dueAt: due ? zonedInputToISO(due, timezone) : null,
        }),
      });
      await recognitions.refetch();
      setForm(null);
    } catch (caught) {
      setError(caught);
    } finally {
      setWorking(false);
    }
  }

  function updateContribution(item: Contribution, status: string) {
    if (status !== item.status) setTransition({ kind: "contribution", item, status });
  }

  function updateRecognition(item: Recognition, status: string) {
    if (status !== item.status) setTransition({ kind: "recognition", item, status });
  }

  async function requestPayment(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!requesting) return;
    const data = new FormData(event.currentTarget);
    const due = String(data.get("dueAt") ?? "");
    setWorking(true);
    setError(undefined);
    try {
      const invoice = await api<{ invoiceId: string; invoiceNumber: string }>(
        `/api/v1/contributions/${requesting.id}/invoice`,
        {
          method: "POST",
          body: jsonBody({
            customerId: String(data.get("customerId") ?? ""),
            dueAt: due ? new Date(`${due}T00:00:00Z`).toISOString() : null,
          }),
        },
      );
      // The pledge is invoiced now whatever happens to the link, so the list says so first.
      await contributions.refetch();
      const link = await api<{ url: string }>(
        `/api/v1/invoices/${invoice.invoiceId}/payment-links`,
        {
          method: "POST",
          body: jsonBody({ scenario: "success" }),
        },
      );
      setPaymentLink({ url: link.url, invoiceNumber: invoice.invoiceNumber });
      setRequesting(null);
    } catch (caught) {
      setError(caught);
    } finally {
      setWorking(false);
    }
  }

  async function submitTransition(reason: string) {
    if (!transition) return;
    setWorking(true);
    setError(undefined);
    try {
      if (transition.kind === "contribution") {
        await api(`/api/v1/contributions/${transition.item.id}/status`, {
          method: "PUT",
          body: jsonBody({ status: transition.status, reason, version: transition.item.version }),
        });
        await contributions.refetch();
      } else {
        await api(`/api/v1/recognitions/${transition.item.id}/status`, {
          method: "PUT",
          body: jsonBody({ status: transition.status, reason, version: transition.item.version }),
        });
        await recognitions.refetch();
      }
      setTransition(null);
    } catch (caught) {
      setError(caught);
    } finally {
      setWorking(false);
    }
  }

  if (form) {
    const formTitle =
      form === "sponsor"
        ? "Add sponsor"
        : form === "agreement"
          ? "Add sponsorship agreement"
          : form === "contribution"
            ? "Add contribution"
            : "Add recognition obligation";
    return (
      <section className="editor-page">
        <PageHeader
          title={formTitle}
          description={
            form === "contribution" || form === "recognition"
              ? selected?.name
              : "Sponsor administration"
          }
          actions={
            <button className="button secondary" type="button" onClick={() => setForm(null)}>
              Back to sponsors
            </button>
          }
        />
        {error ? <ErrorPanel error={error} /> : null}
        <Card>
          {form === "sponsor" ? (
            <form className="form-grid" onSubmit={createSponsor}>
              <Field label="Sponsor name">
                <input name="name" required />
              </Field>
              <Field label="Contact name">
                <input name="contactName" />
              </Field>
              <Field label="Email">
                <input name="email" type="email" />
              </Field>
              <Field label="Phone">
                <input name="phone" />
              </Field>
              <Field label="Venue association">
                <select name="venueId">
                  <option value="">All / none</option>
                  {venues.data?.items.map((venue) => (
                    <option key={venue.id} value={venue.id}>
                      {venue.name}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Notes">
                <textarea name="notes" />
              </Field>
              <div className="full">
                <FormActions submitting={working} onCancel={() => setForm(null)} />
              </div>
            </form>
          ) : null}
          {form === "agreement" ? (
            <form className="form-grid" onSubmit={createAgreement}>
              <Field label="Sponsor">
                <select name="sponsorId" required defaultValue="">
                  <option value="" disabled>
                    Select sponsor
                  </option>
                  {sponsors.data?.items.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Agreement name">
                <input name="name" required />
              </Field>
              <Field label="Agreement number">
                <input name="agreementNumber" />
              </Field>
              <Field label="Status">
                <select name="status" defaultValue="active">
                  <option value="pledged">Pledged</option>
                  <option value="active">Active</option>
                  <option value="fulfilled">Fulfilled</option>
                  <option value="cancelled">Canceled</option>
                </select>
              </Field>
              <Field label="Venue">
                <select name="venueId">
                  <option value="">Not venue-specific</option>
                  {venues.data?.items.map((venue) => (
                    <option key={venue.id} value={venue.id}>
                      {venue.name}
                    </option>
                  ))}
                </select>
              </Field>
              <Field
                label="Event"
                hint="The client's own event this sponsors. Leave blank when the agreement is a standing one with a venue."
              >
                <select name="eventId" defaultValue="">
                  <option value="">Not tied to an event</option>
                  {events.data?.items.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.reference} · {item.title}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Starts">
                <input name="startsOn" type="date" />
              </Field>
              <Field label="Ends">
                <input name="endsOn" type="date" />
              </Field>
              <Field label="Renewal reminder">
                <input name="renewalOn" type="date" />
              </Field>
              <Field label="Restrictions">
                <textarea name="restrictions" />
              </Field>
              <div className="full">
                <FormActions submitting={working} onCancel={() => setForm(null)} />
              </div>
            </form>
          ) : null}
          {form === "contribution" ? (
            <form className="form-grid" onSubmit={createContribution}>
              <Field label="Contribution type">
                <select
                  value={kind}
                  onChange={(event) => setKind(event.target.value as "cash" | "in_kind")}
                >
                  <option value="cash">Cash</option>
                  <option value="in_kind">Gifted inventory / in kind</option>
                </select>
              </Field>
              <Field label="Description">
                <textarea name="description" required />
              </Field>
              <Field
                label={
                  kind === "cash"
                    ? `Cash amount (${tenantCurrency})`
                    : `Estimated fair value (${tenantCurrency})`
                }
              >
                <input
                  name="value"
                  type="number"
                  min={kind === "cash" ? 0.01 : 0}
                  step={0.01}
                  required
                />
              </Field>
              {kind === "in_kind" ? (
                <>
                  <Field label="Quantity">
                    <input name="quantity" type="number" min={0.001} step={0.001} />
                  </Field>
                  <Field label="Valuation source">
                    <input
                      name="valuationSource"
                      required
                      placeholder="Supplier quote, market price, donor declaration…"
                    />
                  </Field>
                </>
              ) : null}
              <Field label="Status">
                <select name="status" defaultValue={kind === "cash" ? "pledged" : "accepted"}>
                  {kind === "cash" ? (
                    <>
                      <option value="pledged">Pledged</option>
                      <option value="invoiced">Invoiced</option>
                      <option value="scheduled">Scheduled</option>
                      <option value="received">Received</option>
                    </>
                  ) : (
                    <>
                      <option value="accepted">Accepted</option>
                      <option value="received">Received</option>
                      <option value="consumed">Consumed</option>
                    </>
                  )}
                </select>
              </Field>
              <Field label="Available on">
                <input name="availableOn" type="date" />
              </Field>
              <label className="check-field">
                <input name="restricted" type="checkbox" /> Restricted to a stated purpose
              </label>
              <div className="full">
                <FormActions submitting={working} onCancel={() => setForm(null)} />
              </div>
            </form>
          ) : null}
          {form === "recognition" ? (
            <form className="form-grid" onSubmit={createRecognition}>
              <Field label="Type">
                <select name="type" defaultValue="signage">
                  {[
                    "logo",
                    "signage",
                    "naming",
                    "hospitality",
                    "mention",
                    "tickets",
                    "reporting",
                    "exclusivity",
                    "other",
                  ].map((type) => (
                    <option key={type} value={type}>
                      {titleCase(type)}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Description">
                <textarea name="description" required />
              </Field>
              <Field label="Due at">
                <input name="dueAt" type="datetime-local" />
              </Field>
              <div className="full">
                <FormActions submitting={working} onCancel={() => setForm(null)} />
              </div>
            </form>
          ) : null}
        </Card>
      </section>
    );
  }

  return (
    <>
      <PageHeader
        title="Sponsors"
        description="Track money and gifted inventory accurately without mixing in-kind value into the cash budget."
        actions={
          <div className="inline">
            <button className="button secondary" type="button" onClick={() => setForm("sponsor")}>
              <Plus size={17} /> Sponsor
            </button>
            <button className="button primary" type="button" onClick={() => setForm("agreement")}>
              <Handshake size={17} /> Agreement
            </button>
          </div>
        }
      />
      <Notice tone="info" title="Clear accounting treatment">
        Cash contributions affect cash budget visibility. In-kind estimated fair value is reported
        separately and never presented as spendable money.
      </Notice>
      {paymentLink ? (
        <Notice tone="success" title={`Invoice ${paymentLink.invoiceNumber} issued`}>
          <a href={paymentLink.url} target="_blank" rel="noreferrer">
            Open the secure payment page
          </a>{" "}
          and send it to the sponsor. The pledge shows as received when the money lands.
        </Notice>
      ) : null}
      <div className="content-grid">
        <Card className="span-5">
          <CardHeader
            title="Agreements"
            description={`${sponsorships.data?.items.length ?? 0} sponsorships`}
          />
          {sponsorships.data?.items.length ? (
            <div className="tenant-list">
              {sponsorships.data.items.map((item) => (
                <button
                  className="tenant-choice"
                  type="button"
                  key={item.id}
                  onClick={() => setSelected(item)}
                >
                  <span>
                    <strong>{item.name}</strong>
                    <small>
                      {item.eventId
                        ? "For an event"
                        : item.bookingId
                          ? "For a booking"
                          : "Venue-wide"}{" "}
                      · {item.agreementNumber || "No agreement number"}
                    </small>
                  </span>
                  <StatusPill value={item.status} />
                </button>
              ))}
            </div>
          ) : (
            <EmptyState
              title="No agreements"
              description="Add a sponsor and create the first agreement."
            />
          )}
        </Card>
        <div className="span-7 stack">
          {selected ? (
            <>
              <Card>
                <CardHeader
                  title={selected.name}
                  actions={<StatusPill value={selected.status} />}
                />
                <dl className="definition-list">
                  <dt>Agreement</dt>
                  <dd>{selected.agreementNumber || "Not available"}</dd>
                  <dt>Starts</dt>
                  <dd>{formatDate(selected.startsOn)}</dd>
                  <dt>Ends</dt>
                  <dd>{formatDate(selected.endsOn)}</dd>
                  <dt>Renewal</dt>
                  <dd>{formatDate(selected.renewalOn)}</dd>
                  <dt>Covers</dt>
                  <dd>
                    {selected.eventId
                      ? (events.data?.items.find((item) => item.id === selected.eventId)?.title ??
                        "An event outside the loaded range")
                      : selected.bookingId
                        ? "A booking"
                        : "Every event at the venue"}
                  </dd>
                </dl>
              </Card>
              <div className="metrics-grid">
                <Metric label="Cash pledged" value={formatMoney(cashPledged, tenantCurrency)} />
                <Metric label="Cash received" value={formatMoney(cashReceived, tenantCurrency)} />
                <Metric
                  label="In-kind fair value"
                  value={formatMoney(inKindValue, tenantCurrency)}
                />
                <Metric label="Recognition tasks" value={recognitions.data?.items.length ?? 0} />
              </div>
            </>
          ) : (
            <EmptyState
              title="Select an agreement"
              description="Choose a sponsorship to review contributions and recognition obligations."
            />
          )}
        </div>
        {selected ? (
          <>
            <Card className="span-7">
              <CardHeader
                title="Contributions"
                actions={
                  <button
                    className="button primary small"
                    type="button"
                    onClick={() => setForm("contribution")}
                  >
                    <HandCoins size={16} /> Add contribution
                  </button>
                }
              />
              {allContributions.length ? (
                <DataTable
                  caption="Sponsor contributions"
                  items={allContributions}
                  getKey={(item) => item.id}
                  columns={[
                    {
                      label: "Contribution",
                      render: (item) => (
                        <>
                          <strong>{item.description}</strong>
                          <br />
                          <small>{titleCase(item.kind)}</small>
                        </>
                      ),
                    },
                    {
                      label: "Cash",
                      className: "numeric",
                      render: (item) =>
                        item.kind === "cash"
                          ? formatMoney(item.cashAmountMinor, item.currency)
                          : "Not cash",
                    },
                    {
                      label: "In-kind value",
                      className: "numeric",
                      render: (item) =>
                        item.kind === "in_kind"
                          ? formatMoney(item.estimatedValueMinor, item.currency)
                          : "Not available",
                    },
                    { label: "Quantity", render: (item) => formatQuantity(item.quantityMilli) },
                    {
                      label: "Collect",
                      render: (item) =>
                        item.kind === "cash" && item.status === "pledged" && !item.invoiceId ? (
                          <button
                            className="button primary small"
                            type="button"
                            disabled={working}
                            onClick={() => {
                              setError(undefined);
                              setPaymentLink(null);
                              setRequesting(item);
                            }}
                          >
                            Request payment
                          </button>
                        ) : item.invoiceNumber ? (
                          <span className="muted">{item.invoiceNumber}</span>
                        ) : (
                          <span className="muted">Not available</span>
                        ),
                    },
                    {
                      label: "Status",
                      render: (item) => (
                        <select
                          value={item.status}
                          disabled={working || contributionTransitions[item.status]?.length === 1}
                          aria-label={`Status for ${item.description}`}
                          onChange={(event) => updateContribution(item, event.target.value)}
                        >
                          {(contributionTransitions[item.status] ?? [item.status]).map((status) => (
                            <option key={status} value={status}>
                              {titleCase(status)}
                            </option>
                          ))}
                        </select>
                      ),
                    },
                  ]}
                />
              ) : (
                <EmptyState
                  title="No contributions"
                  description="Record a cash pledge or an in-kind gift."
                />
              )}
            </Card>
            <Card className="span-5">
              <CardHeader
                title="Recognition obligations"
                actions={
                  <button
                    className="button secondary small"
                    type="button"
                    onClick={() => setForm("recognition")}
                  >
                    <Gift size={16} /> Add obligation
                  </button>
                }
              />
              {recognitions.data?.items.length ? (
                <div className="stack">
                  {recognitions.data.items.map((item) => (
                    <div key={item.id} className="split">
                      <span>
                        <strong>{item.description}</strong>
                        <small>
                          {titleCase(item.type)} · due {formatDate(item.dueAt)}
                        </small>
                      </span>
                      <select
                        value={item.status}
                        disabled={working || recognitionTransitions[item.status]?.length === 1}
                        aria-label={`Status for ${item.description}`}
                        onChange={(event) => updateRecognition(item, event.target.value)}
                      >
                        {(recognitionTransitions[item.status] ?? [item.status]).map((status) => (
                          <option key={status} value={status}>
                            {titleCase(status)}
                          </option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>
              ) : (
                <EmptyState
                  title="No obligations"
                  description="Track signage, mentions, hospitality, and deliverables owed to the sponsor."
                />
              )}
            </Card>
          </>
        ) : null}
      </div>

      <Modal
        open={requesting !== null}
        onClose={() => setRequesting(null)}
        title="Request payment"
        description={
          requesting
            ? `Issue an invoice for “${requesting.description}” and collect it with a secure payment link.`
            : undefined
        }
      >
        {error ? <ErrorPanel error={error} /> : null}
        {requesting ? (
          <form className="form-stack" onSubmit={requestPayment}>
            <Field
              label="Bill to"
              hint="The person or organization at the sponsor who pays. Create them as a contact here if they are new."
            >
              <ContactPicker autoFocus name="customerId" required />
            </Field>
            <Field label="Due date" hint="Optional. Left empty the invoice carries no due date.">
              <input type="date" name="dueAt" />
            </Field>
            <Notice tone="info">
              {formatMoney(requesting.cashAmountMinor, requesting.currency)} is invoiced immediately
              and the pledge is marked invoiced. It shows as received when the sponsor pays the link
              or a payment is recorded on the invoice.
            </Notice>
            <FormActions
              submitting={working}
              submitLabel="Issue invoice and get link"
              onCancel={() => setRequesting(null)}
            />
          </form>
        ) : null}
      </Modal>

      <ReasonDialog
        open={transition !== null}
        title={
          transition
            ? `Mark ${transition.kind} ${titleCase(transition.status)}`
            : "Update sponsor record"
        }
        description={
          transition
            ? `Record why “${transition.item.description}” is being marked ${titleCase(transition.status)}.`
            : undefined
        }
        submitLabel={transition ? `Mark ${titleCase(transition.status)}` : "Update"}
        submitting={working}
        tone={
          transition &&
          ["cancelled", "refunded", "returned", "written_off", "waived"].includes(transition.status)
            ? "danger"
            : "primary"
        }
        onClose={() => setTransition(null)}
        onSubmit={submitTransition}
      />
    </>
  );
}
